import { memo, useCallback, useMemo, useRef } from 'react';

import { Badge } from '@air/primitive-badge';
import { cn } from '@air/utils-tailwind';

import { PrivateWorkspaceAgentSessionTitleRenameForm } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/PrivateWorkspaceAgentSessionTitleRenameForm';
import { useWorkspaceAgentOnboardingWinningTimeSink } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/onboarding/useWorkspaceAgentOnboardingWinningTimeSink';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type WorkspaceAgentPanelView } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelView';
import { getWorkspaceAgentPanelHeaderDisplayTitle } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelHeaderDisplayTitle';
import { WORKSPACE_AGENT_DEFAULT_SESSION_TITLE } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';

export type PrivateWorkspaceAgentPanelTaskHeaderTitleProps = {
  isRenaming: boolean;
  onRenamingChange: (isRenaming: boolean) => void;
  panelView: WorkspaceAgentPanelView;
};

export const PrivateWorkspaceAgentPanelTaskHeaderTitle = memo(
  ({ isRenaming, onRenamingChange, panelView }: PrivateWorkspaceAgentPanelTaskHeaderTitleProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const { winningTimeSink } = useWorkspaceAgentOnboardingWinningTimeSink();
    const renameBaselineRef = useRef<{ sessionId: string; title: string } | undefined>();

    const activeSession = useMemo(
      () => (session.sessionId ? session.sessions.find((entry) => entry.id === session.sessionId) : undefined),
      [session.sessionId, session.sessions],
    );
    const activeSessionTitle = activeSession?.title;
    const currentTitle = useMemo(() => activeSessionTitle?.trim() ?? '', [activeSessionTitle]);
    const editableTitle = currentTitle || WORKSPACE_AGENT_DEFAULT_SESSION_TITLE;
    const sessionDisplayTitle = getWorkspaceAgentPanelHeaderDisplayTitle({
      panelView,
      sessionId: session.sessionId,
      sessionTitle: editableTitle,
    });
    const displayTitle =
      panelView === 'chat' && !session.sessionId && winningTimeSink ? winningTimeSink.panelTitle : sessionDisplayTitle;
    const showBetaBadge = panelView === 'tasks' || displayTitle === WORKSPACE_AGENT_DEFAULT_SESSION_TITLE;

    if (!isRenaming || !session.sessionId) {
      renameBaselineRef.current = undefined;
    } else if (renameBaselineRef.current?.sessionId !== session.sessionId) {
      renameBaselineRef.current = { sessionId: session.sessionId, title: editableTitle };
    }

    const renameBaselineTitle = renameBaselineRef.current?.title;

    const handleRenameCancel = useCallback(() => {
      onRenamingChange(false);
    }, [onRenamingChange]);

    const handleRenameSuccess = useCallback(() => {
      onRenamingChange(false);
    }, [onRenamingChange]);

    return (
      <div className="relative flex min-w-0 flex-1 items-center gap-2 font-semibold ads:text-primary text-14 text-gray-12 leading-5">
        <span
          aria-hidden={isRenaming}
          className={cn('min-w-0 truncate', isRenaming && 'invisible')}
          data-testid="WORKSPACE_AGENT_PANEL_HEADER_TITLE"
        >
          {displayTitle}
        </span>
        {showBetaBadge && !isRenaming ? (
          <Badge className="shrink-0" data-testid="WORKSPACE_AGENT_PANEL_BETA_BADGE">
            Beta
          </Badge>
        ) : null}
        {isRenaming && session.sessionId && renameBaselineTitle ? (
          <PrivateWorkspaceAgentSessionTitleRenameForm
            baselineTitle={renameBaselineTitle}
            defaultTitle={renameBaselineTitle}
            key={session.sessionId}
            onCancel={handleRenameCancel}
            onSuccess={handleRenameSuccess}
            sessionId={session.sessionId}
            workspaceId={session.workspaceId}
          />
        ) : null}
      </div>
    );
  },
);

PrivateWorkspaceAgentPanelTaskHeaderTitle.displayName = 'PrivateWorkspaceAgentPanelTaskHeaderTitle';
